/**
 * GPS Map Module - Leaflet-based map panel for PTT tablet/desktop
 * Dynamically loads Leaflet, connects to GPS WebSocket, renders course overlay
 */

function escapeHtml(s) {
  return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;');
}

const LEAFLET_CSS = './leaflet.css';
const LEAFLET_JS = './leaflet.js';
const WS_URL = `wss://dashboard.livefusion.app/gps/ws`;
const COURSE_URL = 'https://dashboard.livefusion.app/gps/active-course';
const RECONNECT_DELAY = 5000;

export class GPSMap {
  constructor(containerId, username, options = {}) {
    this.containerId = containerId;
    this.username = username;
    this._getToken = options.getToken || (() => null);
    this.map = null;
    this.ws = null;
    this.markers = new Map(); // deviceId -> marker
    this.courseLayer = null;
    this._destroyed = false;
    this._reconnectTimer = null;
    this._leafletLoaded = false;

    // Stale device filtering - hide devices with no update in this many ms
    this._maxAge = options.maxAge || 12 * 60 * 60 * 1000; // 12 hours default

    // Team ID - show edge devices assigned to this team
    this._teamId = options.teamId ?? null;

    // Channel member filter - only show GPS for users in the same Mumble channel
    this._channelMembers = null; // null = show all, Set = filter by name

    // Status cards
    this._statusCardsCollapsed = localStorage.getItem('ptt_status_cards_collapsed') === 'true';
  }

  /**
   * Set the list of usernames in the current Mumble channel.
   * GPS devices will be filtered to only show these users.
   */
  setChannelMembers(names) {
    if (!names || names.length === 0) {
      this._channelMembers = null;
      return;
    }
    this._channelMembers = new Set(names.map(n => n.toLowerCase()));
  }

  async init() {
    if (this._destroyed) return;
    await this._loadLeaflet();
    if (this._destroyed) return;
    this._initMap();
    this._connectWebSocket();
    this._loadCourse();
  }

  destroy() {
    this._destroyed = true;

    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }

    if (this.map) {
      this.map.remove();
      this.map = null;
    }

    this.markers.clear();
    this.courseLayer = null;
  }

  // --- Leaflet dynamic loading ---

  async _loadLeaflet() {
    if (window.L) {
      this._leafletLoaded = true;
      return;
    }

    // Load CSS
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }

    // Load JS
    await new Promise((resolve, reject) => {
      if (window.L) { resolve(); return; }
      const script = document.createElement('script');
      script.src = LEAFLET_JS;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });

    this._leafletLoaded = true;
  }

  // --- Map initialization ---

  _initMap() {
    const container = document.getElementById(this.containerId);
    if (!container || !window.L) return;

    this.map = L.map(this.containerId, {
      zoomControl: true,
      attributionControl: false
    }).setView([39.5, -98.35], 4);

    // Locate control — zoom to user's position
    const LocateControl = L.Control.extend({
      options: { position: 'topleft' },
      onAdd: () => {
        const btn = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
        btn.innerHTML = `<a href="#" title="Zoom to my location" role="button" style="
          display:flex;align-items:center;justify-content:center;
          width:34px;height:34px;cursor:pointer;
          background:#fff;text-decoration:none;
        "><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#333" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/></svg></a>`;
        L.DomEvent.disableClickPropagation(btn);
        btn.querySelector('a').addEventListener('click', (e) => {
          e.preventDefault();
          this.map.locate({ setView: true, maxZoom: 15 });
        });
        return btn;
      }
    });
    new LocateControl().addTo(this.map);

    // Tile layers
    const satellite = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.esri.com">Esri</a>'
    });
    const topo = L.tileLayer('https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', {
      maxZoom: 17,
      className: 'dark-tiles',
      attribution: '&copy; <a href="https://openstreetmap.org">OSM</a> &copy; <a href="https://opentopomap.org">OpenTopoMap</a>'
    });

    const saved = localStorage.getItem('ptt_map_layer') || 'satellite';
    this._activeLayerKey = saved;
    (saved === 'topo' ? topo : satellite).addTo(this.map);

    // Layer switcher control
    const layers = { satellite, topo };
    const self = this;
    const LayerControl = L.Control.extend({
      options: { position: 'topright' },
      onAdd: () => {
        const wrap = L.DomUtil.create('div', 'leaflet-bar leaflet-control gps-layer-control');
        const select = L.DomUtil.create('select', '', wrap);
        select.innerHTML = '<option value="satellite">Satellite</option><option value="topo">Topo</option>';
        select.value = saved;
        L.DomEvent.disableClickPropagation(wrap);
        select.addEventListener('change', () => {
          const key = select.value;
          self.map.removeLayer(layers[self._activeLayerKey]);
          layers[key].addTo(self.map);
          self._activeLayerKey = key;
          localStorage.setItem('ptt_map_layer', key);
        });
        return wrap;
      }
    });
    new LayerControl().addTo(this.map);
  }

  // --- WebSocket connection ---

  _connectWebSocket() {
    if (this._destroyed) return;

    try {
      const token = this._getToken();
      const url = token ? `${WS_URL}?token=${encodeURIComponent(token)}` : WS_URL;
      this.ws = new WebSocket(url);
    } catch (e) {
      console.warn('[GPSMap] WebSocket creation failed:', e);
      this._scheduleReconnect();
      return;
    }

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'initial' || data.type === 'position_update') {
          this._updateMarkers(data.devices);
        }
      } catch (e) {
        console.warn('[GPSMap] WS message parse error:', e);
      }
    };

    this.ws.onclose = () => {
      if (!this._destroyed) {
        console.log('[GPSMap] WebSocket closed, reconnecting...');
        this._scheduleReconnect();
      }
    };

    this.ws.onerror = (e) => {
      console.warn('[GPSMap] WebSocket error:', e);
    };
  }

  _scheduleReconnect() {
    if (this._destroyed || this._reconnectTimer) return;
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      this._connectWebSocket();
    }, RECONNECT_DELAY);
  }

  // --- Marker management ---

  _updateMarkers(devices) {
    if (!this.map || !window.L) return;

    // Filter out stale devices (no update within maxAge)
    const now = Date.now();
    let fresh = devices.filter(d => {
      if (!d.lastUpdate) return false;
      try {
        return (now - new Date(d.lastUpdate).getTime()) < this._maxAge;
      } catch { return false; }
    });

    // Filter to only devices matching current channel members OR same-team edge devices
    if (this._channelMembers) {
      fresh = fresh.filter(d => {
        // Show edge devices (numeric uniqueId) assigned to this team
        const isEdge = /^\d+$/.test(String(d.uniqueId ?? ''));
        if (this._teamId != null && isEdge && String(d.teamId) === String(this._teamId)) return true;
        const name = (d.name || '').toLowerCase();
        return this._channelMembers.has(name);
      });
    }

    // Update status cards with filtered list
    this._updateStatusCards(fresh);

    const activeIds = new Set();

    for (const device of fresh) {
      if (!device.position || device.position.latitude == null) continue;

      const id = device.id;
      activeIds.add(id);
      const lat = device.position.latitude;
      const lng = device.position.longitude;
      const rawName = device.name || `Device ${id}`;
      const name = rawName.replace(/^guest-/i, '');
      const speed = device.position.speedMph || 0;
      const isSelf = this.username && rawName.toLowerCase() === this.username.toLowerCase();
      const isOnline = device.status === 'online';
      const isEdge = /^\d+$/.test(device.uniqueId || '');
      const initial = name.charAt(0).toUpperCase();

      const zOffset = isSelf ? 1000 : isOnline ? 500 : 0;

      if (this.markers.has(id)) {
        // Update existing marker position
        const marker = this.markers.get(id);
        marker.setLatLng([lat, lng]);
        marker.setIcon(this._createMarkerIcon(initial, isSelf, isOnline, isEdge));
        marker.setZIndexOffset(zOffset);
        marker.setPopupContent(this._popupContent(name, speed, device.lastUpdate, isOnline));
      } else {
        // Create new marker
        const marker = L.marker([lat, lng], {
          icon: this._createMarkerIcon(initial, isSelf, isOnline, isEdge),
          zIndexOffset: zOffset
        }).addTo(this.map);

        marker.bindPopup(this._popupContent(name, speed, device.lastUpdate, isOnline));
        this.markers.set(id, marker);
      }
    }

    // Remove markers for devices no longer present
    for (const [id, marker] of this.markers) {
      if (!activeIds.has(id)) {
        marker.remove();
        this.markers.delete(id);
      }
    }

    // Auto-fit bounds on first data
    if (this.markers.size > 0 && !this._hasFitted) {
      this._fitBounds();
      this._hasFitted = true;
    }
  }

  _createMarkerIcon(initial, isSelf, isOnline, isEdge = false) {
    const bgColor = !isOnline ? '#666' : isSelf ? '#0D99FF' : '#4ade80';
    const size = isSelf ? 36 : 30;
    const vehicleSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><path d="M9 17h6"/><circle cx="17" cy="17" r="2"/></svg>`;
    const content = isEdge ? vehicleSvg : escapeHtml(initial);

    return L.divIcon({
      className: 'gps-map-marker',
      html: `<div style="
        width:${size}px;height:${size}px;border-radius:50%;
        background:${bgColor};color:#fff;
        display:flex;align-items:center;justify-content:center;
        font-weight:700;font-size:${isSelf ? 14 : 12}px;
        border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4);
        font-family:-apple-system,BlinkMacSystemFont,sans-serif;
      ">${content}</div>`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2]
    });
  }

  _popupContent(name, speed, lastUpdate, isOnline) {
    let timeStr = '';
    if (lastUpdate) {
      try {
        const d = new Date(lastUpdate);
        timeStr = d.toLocaleTimeString();
      } catch { timeStr = ''; }
    }
    const statusLabel = isOnline
      ? '<span style="color:#4ade80;">Online</span>'
      : '<span style="color:#888;">Offline</span>';
    return `<div style="font-family:sans-serif;font-size:13px;">
      <strong>${escapeHtml(name)}</strong> · ${statusLabel}<br>
      ${speed > 0 ? `${escapeHtml(speed)} mph<br>` : ''}
      ${timeStr ? `<span style="color:#888;font-size:11px;">${timeStr}</span>` : ''}
    </div>`;
  }

  _fitBounds() {
    if (!this.map) return;
    const points = [];
    for (const marker of this.markers.values()) {
      points.push(marker.getLatLng());
    }
    if (this.courseLayer) {
      const bounds = this.courseLayer.getBounds();
      if (bounds.isValid()) {
        points.push(bounds.getSouthWest());
        points.push(bounds.getNorthEast());
      }
    }
    if (points.length > 0) {
      this.map.fitBounds(L.latLngBounds(points), { padding: [30, 30], maxZoom: 15 });
    }
  }

  // --- Status cards overlay ---

  _updateStatusCards(devices) {
    const el = document.getElementById('status-cards');
    if (!el) return;

    const onlineCount = devices.filter(d => d.status === 'online').length;

    // Build header if not present
    let header = el.querySelector('.status-cards-header');
    if (!header) {
      header = document.createElement('div');
      header.className = 'status-cards-header';
      header.innerHTML = `<span class="status-cards-title"></span><span class="status-cards-chevron">&#9660;</span>`;
      header.addEventListener('click', () => {
        this._statusCardsCollapsed = !this._statusCardsCollapsed;
        localStorage.setItem('ptt_status_cards_collapsed', this._statusCardsCollapsed);
        el.classList.toggle('collapsed', this._statusCardsCollapsed);
      });
      el.prepend(header);
      el.classList.toggle('collapsed', this._statusCardsCollapsed);
    }

    // Update header count
    header.querySelector('.status-cards-title').textContent = `${onlineCount}/${devices.length} Devices`;

    // Build body if not present
    let body = el.querySelector('.status-cards-body');
    if (!body) {
      body = document.createElement('div');
      body.className = 'status-cards-body';
      el.appendChild(body);
    }

    // Sort: online first, then alphabetical
    const sorted = [...devices].sort((a, b) => {
      const aOnline = a.status === 'online' ? 0 : 1;
      const bOnline = b.status === 'online' ? 0 : 1;
      if (aOnline !== bOnline) return aOnline - bOnline;
      return (a.name || '').localeCompare(b.name || '');
    });

    body.innerHTML = '';
    for (const device of sorted) {
      const name = (device.name || `Device ${device.id}`).replace(/^guest-/i, '');
      const speed = device.position ? (device.position.speedMph || 0) : 0;
      const isOnline = device.status === 'online';
      const lastSeen = this._relativeTime(device.lastUpdate);

      const row = document.createElement('div');
      row.className = 'status-card-row';
      row.innerHTML = `<span class="status-card-dot ${isOnline ? 'online' : 'offline'}"></span><span class="status-card-name">${escapeHtml(name)}</span>${speed > 0 ? `<span class="status-card-speed">${escapeHtml(speed)} mph</span>` : ''}<span class="status-card-time">${escapeHtml(lastSeen)}</span>`;
      body.appendChild(row);
    }
  }

  _relativeTime(isoString) {
    if (!isoString) return '';
    try {
      const then = new Date(isoString).getTime();
      const diff = Math.floor((Date.now() - then) / 1000);
      if (diff < 10) return 'just now';
      if (diff < 60) return `${diff}s ago`;
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
      return `${Math.floor(diff / 86400)}d ago`;
    } catch {
      return '';
    }
  }

  // --- Course overlay ---

  async _loadCourse() {
    try {
      const resp = await fetch(COURSE_URL);
      if (resp.status === 204 || !resp.ok) return;
      const geojson = await resp.json();
      if (!geojson || !geojson.features) return;
      this._renderCourse(geojson);
    } catch (e) {
      console.warn('[GPSMap] Failed to load course:', e);
    }
  }

  _renderCourse(geojson) {
    if (!this.map || !window.L) return;

    if (this.courseLayer) {
      this.courseLayer.remove();
    }

    this.courseLayer = L.geoJSON(geojson, {
      style: (feature) => {
        if (feature.geometry.type === 'LineString' || feature.geometry.type === 'MultiLineString') {
          return {
            color: '#0D99FF',
            weight: 3,
            opacity: 0.8
          };
        }
      },
      pointToLayer: (feature, latlng) => {
        const props = feature.properties || {};
        const cpType = (props.checkpoint_type || props.type || '').toLowerCase();
        const isStartFinish = cpType.includes('start') || cpType.includes('finish');

        return L.circleMarker(latlng, {
          radius: isStartFinish ? 8 : 6,
          fillColor: isStartFinish ? '#ef4444' : '#0D99FF',
          color: '#fff',
          weight: 2,
          fillOpacity: 0.9
        });
      },
      onEachFeature: (feature, layer) => {
        const props = feature.properties || {};
        const name = props.name || props.checkpoint_type || '';
        if (name) {
          layer.bindPopup(`<strong>${escapeHtml(name)}</strong>`);
        }
      }
    }).addTo(this.map);

    // Re-fit bounds with course included
    this._fitBounds();
  }
}
